import { MssGridData } from 'src/hooks/analytics';

import { VENDOR_COLORS } from './constants';
import {
    ChartDataPoint,
    VendorCategory,
    aggregateMssByMajorLabels,
    filterChartDataByVendors,
    validateChartData
} from './dataTransformation';

export interface MssChartDataInput {
    data: MssGridData[];
    filteredTerritories?: string[];
    filteredVendors?: string[];
    isAntiDilutionMode?: boolean; // New parameter for anti-dilution toggle
}

interface ChartDataOutput {
    barChartData: ChartDataPoint[];
    lineChartData: ChartDataPoint[];
    availableVendors: VendorCategory[];
}

/**
 * Generates chart data for MSS tab
 */
export function generateMssChartData(input: MssChartDataInput): ChartDataOutput {
    const { data, filteredTerritories, filteredVendors } = input;

    try {
        // Return empty data if no input data
        if (!data || data.length === 0) {
            return {
                barChartData: [],
                lineChartData: [],
                availableVendors: []
            };
        }

        // Data validation is now handled in aggregateMssByMajorLabels

        // Aggregate data by major labels with manual MSS calculation (includes validation)
        const aggregatedData = aggregateMssByMajorLabels(data, filteredTerritories, filteredVendors);

        if (aggregatedData.length === 0) {
            return {
                barChartData: [],
                lineChartData: [],
                availableVendors: []
            };
        }

        // Convert aggregated data to chart data points
        const chartData: ChartDataPoint[] = aggregatedData.map((item) => ({
            month: item.month,
            UMG: item.UMG,
            SONY: item.SONY,
            WMG: item.WMG,
            Indies: item.Indies,
            'UMG Music-Only': item['UMG Music-Only']
        }));

        // Validate chart data for progressive loading
        const validation = validateChartData(chartData);
        if (!validation.isValid) {
            console.warn('MSS chart data validation issues:', validation.issues);
        }

        // Get available vendors from the chart data
        const { availableVendors } = filterChartDataByVendors(chartData, []);

        const barData = chartData;

        // For MSS line chart, convert percentage values to decimal values (0-1 range) for proper formatting
        const lineData = chartData.map((point) => {
            const convertedPoint: any = {
                ...point,
                UMG: (point.UMG || 0) / 100,
                SONY: (point.SONY || 0) / 100,
                WMG: (point.WMG || 0) / 100,
                Indies: (point.Indies || 0) / 100,
                'UMG Music-Only': (point['UMG Music-Only'] || 0) / 100
            };

            return convertedPoint;
        });

        return {
            barChartData: barData,
            lineChartData: lineData,
            availableVendors
        };
    } catch (error) {
        console.error('Error generating MSS chart data:', error);
        return {
            barChartData: [],
            lineChartData: [],
            availableVendors: []
        };
    }
}

/**
 * Creates chart series configuration for MSS bar chart
 */
export function createMssBarSeries(availableVendors: VendorCategory[]) {
    return availableVendors.map((vendor) => ({
        type: 'bar' as const,
        xKey: 'month',
        yKey: vendor,
        yName: vendor,
        stacked: true,
        fill: VENDOR_COLORS[vendor],
        tooltip: {
            renderer: (params: any) => {
                const { datum, yKey } = params;
                const value = datum[yKey];
                const formattedValue = `${value.toFixed(1)}%`;

                return {
                    title: yKey,
                    content: `MSS: ${formattedValue}`
                };
            }
        }
    }));
}

/**
 * Creates chart series configuration for MSS line chart
 */
export function createMssLineSeries(availableVendors: VendorCategory[]) {
    return availableVendors.map((vendor) => ({
        type: 'line' as const,
        xKey: 'month',
        yKey: vendor,
        yName: vendor,
        marker: {
            enabled: true,
            size: 6
        },
        tooltip: {
            renderer: (params: any) => {
                const { datum, yKey } = params;
                const value = datum[yKey];
                // Convert decimal back to percentage for tooltip display
                const formattedValue = `${(value * 100).toFixed(3)}%`;

                return {
                    title: yKey,
                    content: `MSS: ${formattedValue}`
                };
            }
        }
    }));
}
